import PublishOutlined from "@mui/icons-material/PublishOutlined";
import SaveOutlined from "@mui/icons-material/SaveOutlined";
import OpenInNewOutlined from "@mui/icons-material/OpenInNewOutlined";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import CloseIcon from "@mui/icons-material/Close";
import AddOutlined from "@mui/icons-material/AddOutlined";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Company, Employee, LandingPage, LandingPageChatMessage, WebArtifacts } from "../../types";
import { User, hostname, parseError, isSafeUrl } from "../../utils";
import LoadingButton from "../../components/LoadingButton";
import { useWhitelabel } from "../../context/WhitelabelContext";
import { v4 as uuidv4 } from "uuid";

interface ChatMessage extends LandingPageChatMessage {
  isStreaming?: boolean;
}

interface LandingPageBuilderProps {
  currentUser: User;
  companyId: string;
  company: Company;
  employees: Employee[];
  getToken: () => Promise<string>;
  eventListener: (event: any) => void;
  currentPlan: string;
  pageId: string;
  pageName?: string;
  onClose?: () => void;
}

const LandingPageBuilder: React.FC<LandingPageBuilderProps> = ({
  currentUser,
  companyId,
  company,
  employees,
  getToken,
  eventListener,
  currentPlan,
  pageId,
  pageName,
  onClose,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { whitelabelSettings } = useWhitelabel();

  const [landingPage, setLandingPage] = useState<LandingPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [htmlContent, setHtmlContent] = useState("");
  const [prompt, setPrompt] = useState("");
  const [chat, setChat] = useState<ChatMessage[]>([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatInitialized, setChatInitialized] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsName, setSettingsName] = useState("");
  const [settingsDescription, setSettingsDescription] = useState("");
  const [settingsArtifactsId, setSettingsArtifactsId] = useState("");
  const [settingsListId, setSettingsListId] = useState("");
  const [settingsDomainId, setSettingsDomainId] = useState("");
  const [artifactsList, setArtifactsList] = useState<WebArtifacts[]>([]);
  const [smartLists, setSmartLists] = useState<{ id: string; name: string }[]>([]);
  const [verifiedDomains, setVerifiedDomains] = useState<{ id: string; subdomain: string }[]>([]);
  const [loadingArtifacts, setLoadingArtifacts] = useState(false);
  const [loadingLists, setLoadingLists] = useState(false);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [creatingList, setCreatingList] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const chatContainerRef = useRef<HTMLDivElement | null>(null);

  // Save chat history to backend
  const saveChatHistory = useCallback(async (messages: ChatMessage[]) => {
    try {
      const token = await getToken();
      // Strip out isStreaming flag before saving
      const cleanMessages = messages.map(({ isStreaming, ...rest }) => rest);
      await axios.put(
        `${hostname}/landing-pages/${pageId}/chat-history`,
        { companyId, chatHistory: cleanMessages },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error("Failed to save chat history:", error);
    }
  }, [pageId, companyId, getToken]);

  const fetchLandingPage = useCallback(async (skipChatLoad = false) => {
    if (!pageId) return;

    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/landing-pages/${pageId}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { companyId },
      });
      setLandingPage(response.data);

      // Load chat history from the landing page (only on initial load)
      if (!skipChatLoad && !chatInitialized && response.data.chatHistory?.length > 0) {
        setChat(response.data.chatHistory);
        setChatInitialized(true);
      }

      // Fetch the preview content from the project
      try {
        const previewResponse = await axios.get(
          `${hostname}/landing-pages/${pageId}/preview`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { companyId },
          }
        );
        setHtmlContent(previewResponse.data?.content || "");
      } catch {
        // Fallback to stored htmlContent
        setHtmlContent(response.data.htmlContent || "");
      }
    } catch (error) {
      eventListener({ type: "SET_ERROR", error: parseError(error) });
      onClose?.();
    } finally {
      setLoading(false);
    }
  }, [pageId, companyId, getToken, eventListener, onClose, chatInitialized]);

  useEffect(() => {
    fetchLandingPage();
  }, [fetchLandingPage]);


  const handleSave = async () => {
    if (!pageId) return;

    setSaving(true);
    try {
      const token = await getToken();
      await axios.put(
        `${hostname}/landing-pages/${pageId}/content`,
        { companyId, htmlContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      eventListener({ type: "SET_SUCCESS", message: "Landing page saved" });
    } catch (error) {
      eventListener({ type: "SET_ERROR", error: parseError(error) });
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!pageId) return;

    setPublishing(true);
    try {
      // Save first
      const token = await getToken();
      await axios.put(
        `${hostname}/landing-pages/${pageId}/content`,
        { companyId, htmlContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Then publish
      const response = await axios.post(
        `${hostname}/landing-pages/${pageId}/publish`,
        { companyId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setLandingPage(response.data);
      eventListener({ type: "SET_SUCCESS", message: "Landing page published successfully" });
    } catch (error) {
      eventListener({ type: "SET_ERROR", error: parseError(error) });
    } finally {
      setPublishing(false);
    }
  };

  const fetchArtifactsList = useCallback(async () => {
    setLoadingArtifacts(true);
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/web-artifacts`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { companyId, listAll: true },
      });
      setArtifactsList(response.data.items || []);
    } catch (error) {
      console.error("Failed to fetch artifacts:", error);
    } finally {
      setLoadingArtifacts(false);
    }
  }, [companyId, getToken]);

  const fetchSmartLists = useCallback(async () => {
    setLoadingLists(true);
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/lists`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { companyId, page: 1, pageSize: 100, minimal: true },
      });
      const items = response.data.items || [];
      setSmartLists(items.map((list: { id: string; name: string }) => ({
        id: list.id,
        name: list.name,
      })));
    } catch (error) {
      console.error("Failed to fetch smart lists:", error);
    } finally {
      setLoadingLists(false);
    }
  }, [companyId, getToken]);

  const handleOpenSettings = async () => {
    if (landingPage) {
      setSettingsName(landingPage.name || "");
      setSettingsDescription(landingPage.description || "");
      setSettingsListId(landingPage.listId || "");
      setSettingsDomainId(landingPage.domainId || "");
    }
    setSettingsOpen(true);

    // Fetch artifacts, smart lists, and domains in parallel
    setLoadingArtifacts(true);
    setLoadingLists(true);
    setLoadingDomains(true);

    try {
      const token = await getToken();

      // Fetch all in parallel
      const [artifactsResponse, listsResponse, domainsResponse] = await Promise.all([
        axios.get(`${hostname}/web-artifacts`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { companyId, listAll: true },
        }),
        axios.get(`${hostname}/lists`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { companyId, page: 1, pageSize: 100, minimal: true },
        }),
        axios.get(`${hostname}/tracking-domains`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { companyId },
        }),
      ]);

      // Process artifacts
      const artifactItems = artifactsResponse.data.items || [];
      setArtifactsList(artifactItems);

      // Find the artifacts linked to this landing page
      if (landingPage) {
        const linkedArtifacts = artifactItems.find((a: WebArtifacts) => a.landingPageId === landingPage.id);
        setSettingsArtifactsId(linkedArtifacts?.id || "");
      }

      // Process smart lists
      const listItems = listsResponse.data.items || [];
      setSmartLists(listItems.map((list: { id: string; name: string }) => ({
        id: list.id,
        name: list.name,
      })));

      // Process domains - only show verified ones
      const domainItems = (domainsResponse.data.items || []).filter(
        (d: { status: string }) => d.status === "verified"
      );
      setVerifiedDomains(domainItems.map((d: { id: string; subdomain: string }) => ({
        id: d.id,
        subdomain: d.subdomain,
      })));

    } catch (error) {
      console.error("Failed to fetch settings data:", error);
    } finally {
      setLoadingArtifacts(false);
      setLoadingLists(false);
      setLoadingDomains(false);
    }
  };

  const handleCreateSmartList = async () => {
    if (!landingPage) return;

    setCreatingList(true);
    try {
      const token = await getToken();
      const listName = `${landingPage.name} Leads`;

      const response = await axios.post(
        `${hostname}/lists`,
        {
          companyId,
          listDict: { name: listName, type: 'leads' },
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const newList = response.data;

      // Add to local list and select it
      setSmartLists((prev) => [...prev, { id: newList.id, name: newList.name }]);
      setSettingsListId(newList.id);

      eventListener({ type: "SET_SUCCESS", message: `Created smart list "${listName}"` });
    } catch (error) {
      eventListener({ type: "SET_ERROR", error: parseError(error) });
    } finally {
      setCreatingList(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!pageId || !settingsName.trim()) return;

    setSavingSettings(true);
    try {
      const token = await getToken();

      // Update landing page metadata including listId and domainId
      const response = await axios.patch(
        `${hostname}/landing-pages/${pageId}`,
        {
          companyId,
          name: settingsName.trim(),
          description: settingsDescription.trim() || null,
          listId: settingsListId || "",  // Empty string to remove, or list ID to set
          domainId: settingsDomainId || "",  // Empty string to remove, or domain ID to set
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLandingPage(response.data);

      // Link/unlink artifacts if changed
      if (settingsArtifactsId) {
        await axios.put(
          `${hostname}/web-artifacts`,
          {
            companyId,
            artifactsId: settingsArtifactsId,
            landingPageId: pageId,
            artifacts: artifactsList.find(a => a.id === settingsArtifactsId)?.artifacts || {},
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      setSettingsOpen(false);
      eventListener({ type: "SET_SUCCESS", message: "Settings saved" });
    } catch (error) {
      eventListener({ type: "SET_ERROR", error: parseError(error) });
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSendMessage = async () => {
    if (!prompt.trim() || chatLoading) return;

    const userMessage: ChatMessage = {
      id: uuidv4(),
      role: "user",
      content: prompt,
      timestamp: Date.now(),
    };

    setChat((prev) => [...prev, userMessage]);
    const currentPrompt = prompt;
    setPrompt("");
    setChatLoading(true);

    // Create a placeholder for the streaming response
    const assistantMessageId = uuidv4();
    const assistantMessage: ChatMessage = {
      id: assistantMessageId,
      role: "assistant",
      content: "",
      timestamp: Date.now(),
      isStreaming: true,
    };
    setChat((prev) => [...prev, assistantMessage]);

    try {
      const token = await getToken();

      // Use the new landing page chat endpoint with streaming
      const response = await fetch(
        `${hostname}/landing-pages/${pageId}/chat`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            companyId,
            message: currentPrompt,
            messages: chat.map((m) => ({ role: m.role, content: m.content })),
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to send message");
      }

      // Read the streaming response
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("No response body");
      }

      const decoder = new TextDecoder();
      let fullResponse = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        fullResponse += chunk;

        // Update the assistant message with streaming content
        setChat((prev) =>
          prev.map((m) =>
            m.id === assistantMessageId
              ? { ...m, content: fullResponse }
              : m
          )
        );
      }

      // Mark streaming as complete and save chat history
      setChat((prev) => {
        const updatedChat = prev.map((m) =>
          m.id === assistantMessageId
            ? { ...m, isStreaming: false }
            : m
        );
        // Save chat history after streaming completes
        saveChatHistory(updatedChat);
        return updatedChat;
      });

      // After streaming completes, fetch the updated landing page to get the latest HTML
      // The AI may have written to project files and published
      await fetchLandingPage(true); // skipChatLoad=true to avoid overwriting current chat

    } catch (error) {
      eventListener({ type: "SET_ERROR", error: parseError(error) });
      setChat((prev) =>
        prev.map((m) =>
          m.id === assistantMessageId
            ? {
                ...m,
                content: "Sorry, I encountered an error. Please try again.",
                isStreaming: false,
              }
            : m
        )
      );
    } finally {
      setChatLoading(false);
    }
  };

  const scrollToBottom = (instant = false) => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [chat]);

  // Scroll to bottom when chat history is initially loaded (after loading completes)
  useEffect(() => {
    if (!loading && chatInitialized && chat.length > 0) {
      // Use setTimeout to ensure DOM is fully rendered before scrolling
      setTimeout(() => scrollToBottom(), 100);
    }
  }, [loading, chatInitialized, chat.length]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!landingPage) {
    return (
      <Box textAlign="center" py={4}>
        <Typography color="error">Landing page not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography sx={{ flex: 1 }} variant="h6" component="div">
          {pageName || landingPage.name || "Landing Page Builder"}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Chip
            label={landingPage.status.charAt(0).toUpperCase() + landingPage.status.slice(1)}
            size="small"
            color={landingPage.status === "published" ? "primary" : "default"}
          />
          {landingPage.isPublished && landingPage.publicUrl && (
            <Tooltip title="View Published Page">
              <IconButton
                onClick={() => {
                  const publicUrl = landingPage.publicUrl!;
                  const safeUrlPattern = /^https?:\/\/[a-zA-Z0-9][a-zA-Z0-9.\-:/\\?&=%_+~#]+$/;
                  if (safeUrlPattern.test(publicUrl)) window.open(publicUrl, "_blank");
                }}
                size="small"
              >
                <OpenInNewOutlined />
              </IconButton>
            </Tooltip>
          )}
          <LoadingButton
            variant="outlined"
            startIcon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
            size="small"
          >
            Save
          </LoadingButton>
          <LoadingButton
            variant="contained"
            startIcon={<PublishOutlined />}
            onClick={handlePublish}
            loading={publishing}
            size="small"
          >
            Publish
          </LoadingButton>
          <Tooltip title="Settings">
            <IconButton
              onClick={handleOpenSettings}
              size="small"
            >
              <SettingsOutlined />
            </IconButton>
          </Tooltip>
          <IconButton
            edge="end"
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Split Pane */}
      <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* Left Pane - Chat */}
        <Box
          sx={{
            width: isMobile ? "100%" : "40%",
            minWidth: 320,
            maxWidth: 500,
            display: "flex",
            flexDirection: "column",
            borderRight: 1,
            borderColor: "divider",
          }}
        >
          {/* Chat Messages */}
          <Box ref={chatContainerRef} sx={{ flex: 1, overflow: "auto", p: 2 }}>
            {chat.length === 0 ? (
              <Box textAlign="center" py={4}>
                <Typography color="text.secondary" gutterBottom>
                  Start building your landing page
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Describe what you want to create, and I'll generate the HTML for you.
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Try prompts like:
                  </Typography>
                  <Chip
                    label="Create a product launch page"
                    size="small"
                    sx={{ m: 0.5, cursor: "pointer" }}
                    onClick={() => setPrompt("Create a modern product launch landing page for a new SaaS tool")}
                  />
                  <Chip
                    label="Add a hero section"
                    size="small"
                    sx={{ m: 0.5, cursor: "pointer" }}
                    onClick={() => setPrompt("Add a hero section with a headline, subheadline, and call-to-action button")}
                  />
                  <Chip
                    label="Add pricing table"
                    size="small"
                    sx={{ m: 0.5, cursor: "pointer" }}
                    onClick={() => setPrompt("Add a 3-tier pricing table section")}
                  />
                </Box>
              </Box>
            ) : (
              chat.map((message) => (
                <Box
                  key={message.id}
                  sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: message.role === "user" ? "action.hover" : "background.paper",
                    border: 1,
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                    {message.role === "user" ? "You" : "AI Assistant"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: "pre-wrap",
                      "& code": {
                        bgcolor: "action.hover",
                        px: 0.5,
                        borderRadius: 0.5,
                        fontFamily: "monospace",
                      },
                    }}
                  >
                    {message.content}
                  </Typography>
                </Box>
              ))
            )}
            {chatLoading && (
              <Box display="flex" alignItems="center" gap={1} p={2}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Generating...
                </Typography>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Box>

          {/* Chat Input */}
          <Box sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Describe what you want to create or change..."
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              disabled={chatLoading}
            />
            <Box display="flex" justifyContent="flex-end" mt={1}>
              <LoadingButton
                variant="contained"
                onClick={handleSendMessage}
                loading={chatLoading}
                disabled={!prompt.trim()}
              >
                Send
              </LoadingButton>
            </Box>
          </Box>
        </Box>

        {/* Right Pane - Preview */}
        {!isMobile && (
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", bgcolor: "background.default" }}>
            {/* Preview Header */}
            <Box
              sx={{
                p: 1,
                borderBottom: 1,
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ flex: 1 }}>
                Preview
              </Typography>
              <Tooltip title="Refresh Preview">
                <IconButton
                  size="small"
                  onClick={() => setPreviewKey((k) => k + 1)}
                >
                  <RefreshOutlined />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Preview Iframe */}
            <Box sx={{ flex: 1, position: "relative" }}>
              {htmlContent ? (
                <iframe
                  key={previewKey}
                  ref={iframeRef}
                  title="Landing Page Preview"
                  srcDoc={htmlContent}
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                    backgroundColor: "#fff",
                  }}
                />
              ) : (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  height="100%"
                  bgcolor="action.hover"
                >
                  <Typography color="text.secondary">
                    No content yet. Start chatting to generate your landing page.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Landing Page Settings
          <IconButton size="small" onClick={() => setSettingsOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Page Name"
            value={settingsName}
            onChange={(e) => setSettingsName(e.target.value)}
            sx={{ mt: 1 }}
          />
          <TextField
            fullWidth
            label="Description"
            value={settingsDescription}
            onChange={(e) => setSettingsDescription(e.target.value)}
            sx={{ mt: 2 }}
            multiline
            rows={2}
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="settings-artifacts-label">Brand Kit</InputLabel>
            <Select
              labelId="settings-artifacts-label"
              value={settingsArtifactsId}
              label="Brand Kit"
              onChange={(e) => setSettingsArtifactsId(e.target.value)}
              disabled={loadingArtifacts}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {artifactsList.map((artifact) => (
                <MenuItem key={artifact.id} value={artifact.id}>
                  {artifact.name || "Unnamed"}{artifact.isDefault ? " (Default)" : ""}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Link brand assets (favicon, logo, images) to this landing page
            </FormHelperText>
          </FormControl>
          <Box sx={{ mt: 2, display: "flex", gap: 1, alignItems: "flex-start" }}>
            <FormControl fullWidth>
              <InputLabel id="settings-list-label">Smart List for Form Capture</InputLabel>
              <Select
                labelId="settings-list-label"
                value={settingsListId}
                label="Smart List for Form Capture"
                onChange={(e) => setSettingsListId(e.target.value)}
                disabled={loadingLists}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {smartLists.map((list) => (
                  <MenuItem key={list.id} value={list.id}>
                    {list.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Attach a smart list to capture form submissions. The AI will automatically use this list's webhook for any forms it creates.
              </FormHelperText>
            </FormControl>
            <Tooltip title={`Create new list: "${landingPage?.name} Leads"`}>
              <span>
                <LoadingButton
                  variant="outlined"
                  onClick={handleCreateSmartList}
                  loading={creatingList}
                  disabled={loadingLists}
                  sx={{ minWidth: 48, height: 56, px: 1 }}
                >
                  <AddOutlined />
                </LoadingButton>
              </span>
            </Tooltip>
          </Box>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="settings-domain-label">Custom Domain</InputLabel>
            <Select
              labelId="settings-domain-label"
              value={settingsDomainId}
              label="Custom Domain"
              onChange={(e) => setSettingsDomainId(e.target.value)}
              disabled={loadingDomains}
            >
              <MenuItem value="">
                <em>Default ({whitelabelSettings?.webDomain || window.location.hostname})</em>
              </MenuItem>
              {verifiedDomains.map((domain) => (
                <MenuItem key={domain.id} value={domain.id}>
                  {domain.subdomain}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Use a custom domain for your public URL. Configure domains in Landing Pages &gt; Domains.
            </FormHelperText>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>Cancel</Button>
          <LoadingButton
            variant="contained"
            onClick={handleSaveSettings}
            loading={savingSettings}
            disabled={!settingsName.trim()}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LandingPageBuilder;
